/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';
import { generateUUID } from '../../../util';
import { SiteConfig } from '../../config';
import { BaseVideoPlacement, Quartile } from './index';

import type { CampaignData, RenderedAdData } from '../../../types/index';
import type {
  GenericPrebidVideoAdEvent,
  PrebidAdUnit,
  PrebidApi,
  PrebidVideoAdStartedData,
  PrebidVideoAdTimeData,
  PrebidVideoPlayerResizeData,
} from '../../../types/prebid';
import { getGlobalPrebidObj } from '../../../util';

const logger = Logger.getInstance();

class PrebidVideoPlacement extends BaseVideoPlacement {
  private pbApi: PrebidApi | undefined = undefined;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
  }

  protected setSlot(): void {
    if (this.slot !== null) {
      return;
    }
    this.pbApi = getGlobalPrebidObj();
    if (!this.pbApi) {
      logger.warn('Global Prebid object is not available on the page');
      return;
    }
    super.setSlot();
  }

  protected setSupportedSizes(): void {}

  protected addListeners(): void {
    if (!this.pbApi) {
      return;
    }
    this.pbApi.onEvent('videoPlayerResize', (event: PrebidVideoPlayerResizeData) =>
      this.playerResizeHandler(event),
    );
    this.pbApi.onEvent('videoAdStarted', (event: PrebidVideoAdStartedData) =>
      this.adStartedHandler(event),
    );
    this.pbApi.onEvent('videoAdSkipped', (event: GenericPrebidVideoAdEvent) =>
      this.adSkippedHandler(event),
    );
    this.pbApi.onEvent('videoAdTime', (event: PrebidVideoAdTimeData) =>
      this.adProgressHandler(event),
    );
    // Note: no pause event exists in PrebidEventMap, so the viewable impression
    // continuity clock cannot be reset on pause for this integration.
    this.pbApi.onEvent('videoAdComplete', (event: GenericPrebidVideoAdEvent) =>
      this.adCompleteHandler(event),
    );
  }

  protected addAdInteractionListener(): void {
    if (!this.pbApi) {
      return;
    }
    this.pbApi.onEvent('videoAdClick', () => this.videoAdClickHandler());
  }

  private adStartedHandler(event: PrebidVideoAdStartedData): void {
    if (this.wrappingDomElement?.id !== event.divId || this.adCurrentlyPlaying) {
      return;
    }

    logger.debug(
      `Ad Started event received for ${this.adUnitCode}, data: ${JSON.stringify(event)}`,
    );

    this.adStarted();

    const adId =
      event.wrapperAdIds.length > 0
        ? event.wrapperAdIds[event.wrapperAdIds.length - 1] // Outermost ad ID.
        : event.adId;

    const renderedAdData: RenderedAdData = {
      advertiserId: event.advertiserId || undefined,
      lineItemId: adId || undefined,
      creativeId: event.creativeId || undefined,
      isPrebidAd: false, // How to determine this?
      isProgrammaticBuy: false, // How to determine this?
      responseIdentifier: generateUUID(),
      size: { width: this.playerWidth, height: this.playerHeight },
    };
    this.handleRenderedAdData(renderedAdData);
  }

  private playerResizeHandler(event: PrebidVideoPlayerResizeData): void {
    if (this.wrappingDomElement?.id !== event.divId) {
      return;
    }
    this.playerWidth = event.width;
    this.playerHeight = event.height;
  }

  private videoAdClickHandler(): void {
    if (!this.adCurrentlyPlaying) {
      return;
    }
    this.dispatchAdInteraction();
  }

  private adSkippedHandler(event: GenericPrebidVideoAdEvent): void {
    if (this.wrappingDomElement?.id !== event.divId) {
      return;
    }
    this.adCurrentlyPlaying = false;
  }

  private adCompleteHandler(event: GenericPrebidVideoAdEvent): void {
    if (this.wrappingDomElement?.id !== event.divId) {
      return;
    }

    const currentOpportunity = this.currentOpportunity;
    if (!currentOpportunity) {
      return;
    }

    // Check if this opportunity has already completed
    if (this.completedOpportunityIds.has(currentOpportunity.id)) {
      return;
    }

    this.dispatchAdQuartileEvent(Quartile.FOURTH);
    this.adCurrentlyPlaying = false;
    // Note: completedOpportunityIds is set inside dispatchAdQuartileEvent when FOURTH quartile is dispatched
  }

  private adProgressHandler(data: PrebidVideoAdTimeData): void {
    const delta = 1;
    const currentOpportunity = this.currentOpportunity;
    if (!currentOpportunity) {
      return;
    }

    const opportunityId = currentOpportunity.id;
    const firedQuartiles = this.quartilesFiredByOpportunity.get(opportunityId) || new Set();

    const progress = (data.time / data.duration) * 100 + delta;
    if (progress >= 75 && !firedQuartiles.has(Quartile.THIRD)) {
      this.dispatchAdQuartileEvent(Quartile.THIRD);
    } else if (progress >= 50 && !firedQuartiles.has(Quartile.SECOND)) {
      this.dispatchAdQuartileEvent(Quartile.SECOND);
    } else if (progress >= 25 && !firedQuartiles.has(Quartile.FIRST)) {
      this.dispatchAdQuartileEvent(Quartile.FIRST);
    }
  }
}

export { PrebidVideoPlacement };
